/**
 * Toast Component
 *
 * Phase 4: Integration & Aggregation Components
 *
 * Provides user feedback notifications with the following features:
 * - Success, error, warning, and info message types
 * - Auto-dismiss with configurable duration
 * - Manual dismiss with close button
 * - Action callback support (e.g., "Retry" button)
 * - Multiple toast stacking
 * - Smooth animations (fade in/out)
 * - Position customization (top-right, top-center, bottom-right, etc.)
 *
 * Usage:
 * - Use ToastProvider to wrap your app
 * - Call useToast() hook to show toasts
 *
 * Example:
 * const { showToast } = useToast();
 * showToast({ type: 'success', message: 'Book scanned successfully!' });
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

// =============================================================================
// Types
// =============================================================================

export type ToastType = "success" | "error" | "warning" | "info";
export type ToastPosition =
  | "top-right"
  | "top-center"
  | "bottom-right"
  | "bottom-center";

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastOptions {
  type: ToastType;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  hideToast: (id: string) => void;
  clearAll: () => void;
}

// =============================================================================
// Context
// =============================================================================

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// =============================================================================
// Toast Provider
// =============================================================================

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top-right",
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Track timeout IDs to prevent memory leaks
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts when provider unmounts
      timeoutRefs.current.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutRefs.current.clear();
    };
  }, []);

  /**
   * Hide a specific toast
   */
  const hideToast = useCallback((id: string) => {
    // Clear timeout for this toast to prevent memory leaks
    const timeoutId = timeoutRefs.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Show a new toast notification
   */
  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const duration = options.duration ?? 5000; // Default 5 seconds

      const newToast: ToastMessage = {
        id,
        type: options.type,
        message: options.message,
        duration,
        action: options.action,
      };

      setToasts((prev) => {
        // Limit number of toasts
        const updated = [...prev, newToast];
        if (updated.length > maxToasts) {
          return updated.slice(-maxToasts);
        }
        return updated;
      });

      // Auto-dismiss if duration is set
      if (duration > 0) {
        const timeoutId = setTimeout(() => {
          hideToast(id);
        }, duration);
        // Store timeout ID for cleanup
        timeoutRefs.current.set(id, timeoutId);
      }
    },
    [maxToasts, hideToast],
  );

  /**
   * Clear all toasts
   */
  const clearAll = useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutRefs.current.clear();
    setToasts([]);
  }, []);

  const value: ToastContextValue = {
    showToast,
    hideToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer
        toasts={toasts}
        position={position}
        onDismiss={hideToast}
      />
    </ToastContext.Provider>
  );
};

// =============================================================================
// Toast Container
// =============================================================================

interface ToastContainerProps {
  toasts: ToastMessage[];
  position: ToastPosition;
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  position,
  onDismiss,
}) => {
  const containerStyle = getContainerStyle(position);

  return (
    <div style={containerStyle} data-testid="toast-container">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

// =============================================================================
// Individual Toast
// =============================================================================

interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Fade in animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => {
      clearTimeout(timer);
      // Clean up fade-out timeout if component unmounts
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    // Track timeout in ref for cleanup
    fadeOutTimeoutRef.current = setTimeout(() => onDismiss(toast.id), 300);
  };

  const handleActionClick = () => {
    if (toast.action) {
      toast.action.onClick();
      handleDismiss();
    }
  };

  const toastStyle = getToastStyle(toast.type, isVisible);

  return (
    <div style={toastStyle} data-testid="toast" data-toast-type={toast.type}>
      {/* Icon */}
      <div style={styles.iconContainer}>{getIcon(toast.type)}</div>

      {/* Content */}
      <div style={styles.content}>
        <p style={styles.message} data-testid="toast-message">
          {toast.message}
        </p>
        {toast.action && (
          <button
            onClick={handleActionClick}
            style={styles.actionButton}
            data-testid="toast-action-button"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={handleDismiss}
        style={styles.closeButton}
        aria-label="Dismiss notification"
        data-testid="toast-dismiss"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

// =============================================================================
// Helper Functions
// =============================================================================

const getContainerStyle = (position: ToastPosition): React.CSSProperties => {
  const base: React.CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "16px",
    pointerEvents: "none",
  };

  switch (position) {
    case "top-right":
      return { ...base, top: 0, right: 0 };
    case "top-center":
      return { ...base, top: 0, left: "50%", transform: "translateX(-50%)" };
    case "bottom-right":
      return { ...base, bottom: 0, right: 0 };
    case "bottom-center":
      return { ...base, bottom: 0, left: "50%", transform: "translateX(-50%)" };
    default:
      return { ...base, top: 0, right: 0 };
  }
};

const getToastStyle = (
  type: ToastType,
  isVisible: boolean,
): React.CSSProperties => {
  const base: React.CSSProperties = {
    ...styles.toast,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(-20px)",
    transition: "opacity 0.3s, transform 0.3s",
  };

  switch (type) {
    case "success":
      return {
        ...base,
        backgroundColor: "#ecfdf5",
        borderColor: "#10b981",
        color: "#065f46",
      };
    case "error":
      return {
        ...base,
        backgroundColor: "#fef2f2",
        borderColor: "#ef4444",
        color: "#991b1b",
      };
    case "warning":
      return {
        ...base,
        backgroundColor: "#fffbeb",
        borderColor: "#f59e0b",
        color: "#92400e",
      };
    case "info":
      return {
        ...base,
        backgroundColor: "#eee9fa",
        borderColor: "#6a49a8",
        color: "#2b1a45",
      };
    default:
      return base;
  }
};

const getIcon = (type: ToastType): React.ReactNode => {
  const iconStyle: React.CSSProperties = { width: "20px", height: "20px" };

  switch (type) {
    case "success":
      return (
        <svg
          style={iconStyle}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    case "error":
      return (
        <svg
          style={iconStyle}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ef4444"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case "warning":
      return (
        <svg
          style={iconStyle}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="2"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case "info":
      return (
        <svg
          style={iconStyle}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6a49a8"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
    default:
      return null;
  }
};

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  toast: {
    display: "flex",
    alignItems: "start",
    gap: "12px",
    padding: "16px",
    minWidth: "300px",
    maxWidth: "500px",
    backgroundColor: "#ffffff",
    border: "1px solid",
    borderRadius: "8px",
    boxShadow:
      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    pointerEvents: "auto",
  },
  iconContainer: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  message: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "1.5",
  },
  actionButton: {
    alignSelf: "flex-start",
    padding: "4px 12px",
    backgroundColor: "transparent",
    border: "1px solid currentColor",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  closeButton: {
    flexShrink: 0,
    padding: "4px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.6,
    transition: "opacity 0.2s",
  },
};

export default Toast;
